/*
 * iter-288 — Company detail page (full data pull).
 *
 * Renders every field the admin platform makes available on a
 * `linked_company_files` doc, plus every `service_requests` row tied
 * to the company via `company_registration_id`. Documents and services
 * are split into a 4-tab switch so the page stays scannable.
 *
 * Data sources:
 *   • GET /api/companies/:id            → header, contact, docs, renewals,
 *                                          license_history, linked_accounts
 *   • GET /api/companies/:id/services   → services tab body (extra fetch)
 *
 * Downloads always proxy through /api/companies/documents/... — the
 * session email is authoritative, never the URL.
 *
 * Not rendered (per user request): any `agent_*` field on the file.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, Calendar, Phone, Mail, MapPin, ClipboardList, ArrowLeft,
  AlertCircle, Loader2, Download, Users, FileText, IdCard, PlaneTakeoff,
  FolderOpen, History, Briefcase, Clock, CheckCircle2, ChevronRight,
  ShieldCheck, XCircle, Wallet, Send, PackageCheck, HandCoins, Lock,
  Receipt, Eye, Plus, X, AlertTriangle, Timer,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Navbar } from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import ServiceRequestModal from '../components/ServiceRequestModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Timeline stage order (kept in sync with MyRequests.js).
const STAGE_ORDER = ['pending_review', 'approved', 'in_progress', 'fees_collected',
  'completed', 'commission_collected', 'commission_sent', 'closed'];
const STAGE_LABELS = {
  pending_review:       { ar: 'قيد المراجعة', en: 'Pending review',        Icon: Clock },
  approved:             { ar: 'تمت الموافقة',  en: 'Approved',              Icon: CheckCircle2 },
  in_progress:          { ar: 'قيد التنفيذ',   en: 'In progress',           Icon: Send },
  fees_collected:       { ar: 'تحصيل الرسوم',  en: 'Fees collected',        Icon: Receipt },
  completed:            { ar: 'تم الإنجاز',    en: 'Completed',             Icon: PackageCheck },
  commission_collected: { ar: 'تحصيل العمولة', en: 'Commission collected', Icon: Wallet },
  commission_sent:      { ar: 'إرسال العمولة', en: 'Commission sent',       Icon: HandCoins },
  closed:               { ar: 'مُغلق',         en: 'Closed',                Icon: Lock },
};

export default function CompanyDetail() {
  const { id } = useParams();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [downloading, setDownloading] = useState(null);
  const [activeTab, setActiveTab] = useState('services');
  const [services, setServices] = useState(null);   // null = loading, [] = empty
  const [servicesErr, setServicesErr] = useState('');

  // iter-303 — Inline PDF preview modal
  const [preview, setPreview] = useState(null);
  // iter-303 — Document request modal
  const [docRequestOpen, setDocRequestOpen] = useState(false);
  const [docRequests, setDocRequests] = useState([]);
  // iter-308 — Service request modal
  const [serviceRequestOpen, setServiceRequestOpen] = useState(false);

  const token = () => localStorage.getItem('business_token');
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/companies/${id}`, auth());
        setCompany(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || (language === 'ar' ? 'تعذّر تحميل ملف الشركة' : 'Failed to load company'));
      } finally { setLoading(false); }
    };
    load();
    // Fetch services in parallel — tab body populates as soon as it lands.
    axios.get(`${BACKEND_URL}/api/companies/${id}/services`, auth())
      .then((r) => setServices(r.data || []))
      .catch((e) => setServicesErr(e?.response?.data?.detail || 'Failed'));
    // iter-303 — Pull existing document requests for this company.
    axios.get(`${BACKEND_URL}/api/companies/${id}/document-requests`, auth())
      .then((r) => setDocRequests(Array.isArray(r.data) ? r.data : []))
      .catch(() => { /* non-fatal — tab still works */ });
  }, [id, language]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-GB', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch { return iso; }
  };

  const daysUntil = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const now = new Date();
      return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
    } catch { return null; }
  };

  const downloadFile = async (url, filename, key) => {
    setDownloading(key);
    try {
      const r = await axios.get(url, { ...auth(), responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert(e?.response?.status === 403
        ? (language === 'ar' ? 'ليست لديك صلاحية على هذا الملف' : "You don't have access to this file")
        : (language === 'ar' ? 'تعذّر تحميل الملف' : 'Download failed')
      );
    } finally { setDownloading(null); }
  };

  // iter-303 — Fetch the PDF as a blob and open it in an inline modal.
  // Uses the same authenticated endpoint as the download path, so the
  // auth model stays identical.
  const previewFile = async (url, filename, key) => {
    setDownloading(key);
    try {
      const r = await axios.get(url, { ...auth(), responseType: 'blob' });
      const blob = new Blob([r.data], { type: r.data?.type || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      setPreview({ url: blobUrl, filename: filename || 'document.pdf', downloadUrl: url, downloadKey: key });
    } catch (e) {
      alert(e?.response?.status === 403
        ? (language === 'ar' ? 'ليست لديك صلاحية على هذا الملف' : "You don't have access to this file")
        : (language === 'ar' ? 'تعذّر فتح الملف' : 'Preview failed')
      );
    } finally { setDownloading(null); }
  };

  const closePreview = () => {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  // iter-303 — Submit a new document request. Returns the saved doc so
  // we can prepend it to the local list optimistically.
  const submitDocRequest = async (payload) => {
    const r = await axios.post(
      `${BACKEND_URL}/api/companies/${id}/document-requests`,
      payload,
      auth(),
    );
    setDocRequests((prev) => [r.data, ...prev]);
    return r.data;
  };

  const residencies = (company?.documents?.residencies) || [];
  const visas = (company?.documents?.visas) || [];
  const emiratesIds = (company?.documents?.emirates_ids) || [];
  const otherDocs = (company?.documents?.other_docs) || [];
  const licenseHistory = Array.isArray(company?.license_history) ? company.license_history : [];
  const renewals = Array.isArray(company?.renewals) ? company.renewals : [];
  const linkedAccounts = Array.isArray(company?.linked_accounts) ? company.linked_accounts : [];

  // Merge residencies + visas for the first tab; keep sub-type on each row.
  const resVisas = [
    ...residencies.map(d => ({ ...d, __kind: 'residency', __singular: 'residency' })),
    ...visas.map(d => ({ ...d, __kind: 'visa', __singular: 'visa' })),
  ];

  const TABS = [
    { id: 'services',    labelAr: 'الخدمات',              labelEn: 'Services',              count: services === null ? null : services.length, Icon: Briefcase },
    { id: 'res_visas',   labelAr: 'الإقامات والتأشيرات',    labelEn: 'Residencies & Visas',   count: resVisas.length,     Icon: PlaneTakeoff },
    { id: 'emirates',    labelAr: 'الهويات الإماراتية',    labelEn: 'Emirates IDs',          count: emiratesIds.length,  Icon: IdCard },
    { id: 'other',       labelAr: 'وثائق أخرى',           labelEn: 'Other documents',       count: otherDocs.length,    Icon: FolderOpen },
  ];

  return (
    <div className={`min-h-screen cgm-page`}>
      <Navbar onLogout={() => {
        // iter-302: navigate FIRST, then clear state — no white flash.
        navigate('/', { replace: true });
        localStorage.removeItem('business_token');
        localStorage.removeItem('business_user');
        window.dispatchEvent(new Event('userLoggedOut'));
      }} />
      <div className="flex">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <main className={`flex-1 min-w-0 transition-all duration-300 pb-10 ${sidebarOpen ? (language === 'ar' ? 'lg:mr-64' : 'lg:ml-64') : ''}`}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back */}
            <button
              type="button"
              onClick={() => navigate('/companies')}
              className={`inline-flex items-center gap-1.5 text-sm mb-4 transition-colors ${
                isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="company-detail-back"
            >
              <ArrowLeft className={`h-4 w-4 ${language === 'ar' ? 'rotate-180' : ''}`} />
              {language === 'ar' ? 'رجوع إلى شركاتي' : 'Back to My Companies'}
            </button>

            {loading && (
              <div className="cgm-ai-card text-center !py-10" data-testid="company-detail-loading">
                <Loader2 className="h-8 w-8 animate-spin text-[#38BDF8] mx-auto" />
              </div>
            )}

            {!loading && error && (
              <div className="cgm-ai-card !border-red-300 !bg-red-50 dark:!bg-red-500/10" data-testid="company-detail-error">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            {!loading && !error && company && (
              <div className="space-y-4 cgm-reveal-stagger">
                {/* ─── HEADER ─── */}
                <div className="cgm-ai-card-elevated">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="cgm-icon-glow !p-2.5 cgm-breathe shrink-0">
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h1 className={`text-xl sm:text-2xl font-black truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
                            style={{ fontFamily: 'Cairo, sans-serif' }}
                            data-testid="company-detail-name">
                          {language === 'ar' ? (company.company_name_ar || company.company_name_en) : (company.company_name_en || company.company_name_ar)}
                        </h1>
                        {company.request_number && (
                          <div className={`text-xs font-mono mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {company.request_number}
                          </div>
                        )}
                      </div>
                    </div>
                    <ExpiryBadge expiryDate={company.expiry_date} language={language} daysUntil={daysUntil} />
                  </div>

                  {/* iter-303 — Prominent expiry countdown card */}
                  {company.expiry_date && (
                    <ExpiryCountdownCard
                      expiryDate={company.expiry_date}
                      language={language}
                      isDarkMode={isDarkMode}
                      fmtDate={fmtDate}
                      daysUntil={daysUntil}
                    />
                  )}

                  {/* license fields — hide any empty */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                    <Field label={language === 'ar' ? 'رقم الرخصة' : 'License #'} value={company.license_number} isDarkMode={isDarkMode} />
                    <Field label={language === 'ar' ? 'الرقم الموحّد' : 'Unified #'} value={company.unified_license_number} isDarkMode={isDarkMode} />
                    <Field label={language === 'ar' ? 'الجهة المُصدرة' : 'Authority'} value={company.issuing_authority} isDarkMode={isDarkMode} />
                    <Field label={language === 'ar' ? 'نوع الرخصة' : 'License type'} value={company.license_type} isDarkMode={isDarkMode} />
                    <Field label={language === 'ar' ? 'الشكل القانوني' : 'Legal form'} value={company.legal_form} isDarkMode={isDarkMode} />
                    <Field label={language === 'ar' ? 'رأس المال' : 'Capital'} value={company.capital} isDarkMode={isDarkMode} />
                    <Field label={language === 'ar' ? 'تاريخ الإصدار' : 'Issued'} value={fmtDate(company.issue_date)} isDarkMode={isDarkMode} />
                    <Field label={language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry'} value={fmtDate(company.expiry_date)} isDarkMode={isDarkMode} />
                    <Field label={language === 'ar' ? 'ص.ب' : 'PO Box'} value={company.po_box} isDarkMode={isDarkMode} />
                  </div>

                  {company.has_license_file && (
                    <div className="mt-5 flex flex-col sm:flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => previewFile(
                          `${BACKEND_URL}/api/companies/${id}/license-file`,
                          company.license_file_name || 'trade_license.pdf',
                          'license-preview',
                        )}
                        disabled={downloading === 'license-preview'}
                        className={`flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-lg transition-all disabled:opacity-60 ${
                          isDarkMode
                            ? 'bg-[#38BDF8]/15 text-[#38BDF8] hover:bg-[#38BDF8]/25'
                            : 'bg-sky-50 text-sky-700 hover:bg-sky-100'
                        }`}
                        data-testid="preview-license-btn"
                      >
                        {downloading === 'license-preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                        {language === 'ar' ? 'معاينة الرخصة' : 'Preview license'}
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadFile(
                          `${BACKEND_URL}/api/companies/${id}/license-file`,
                          company.license_file_name || 'trade_license.pdf',
                          'license',
                        )}
                        disabled={downloading === 'license'}
                        className={`flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-lg transition-all disabled:opacity-60 ${
                          isDarkMode
                            ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        }`}
                        data-testid="download-license-btn"
                      >
                        {downloading === 'license' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        {language === 'ar' ? 'تحميل الرخصة (PDF)' : 'Download license (PDF)'}
                      </button>
                    </div>
                  )}

                  {/* iter-308 — Service request CTA + go-to list */}
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => setServiceRequestOpen(true)}
                      className="cgm-cta-primary flex-1"
                      data-testid="open-service-request-btn"
                    >
                      <Briefcase className="h-4 w-4" />
                      {language === 'ar' ? 'طلب إدراج خدمة' : 'Request a Service'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/service-requests')}
                      className="cgm-btn-secondary flex-1"
                      data-testid="go-to-service-requests-btn"
                    >
                      <ClipboardList className="h-4 w-4" />
                      {language === 'ar' ? 'طلباتي السابقة' : 'My requests'}
                    </button>
                  </div>
                </div>

                {/* ─── ADDRESS & CONTACT (only if any field present) ─── */}
                {(company.address || company.phone || company.email || company.activities) && (
                  <div className="cgm-ai-card">
                    <SectionTitle Icon={MapPin} label={language === 'ar' ? 'العنوان والتواصل' : 'Address & contact'} isDarkMode={isDarkMode} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {company.address && <FieldIcon Icon={MapPin} label={language === 'ar' ? 'العنوان' : 'Address'} value={company.address} isDarkMode={isDarkMode} />}
                      {company.phone && <FieldIcon Icon={Phone} label={language === 'ar' ? 'الهاتف' : 'Phone'} value={company.phone} isDarkMode={isDarkMode} />}
                      {company.email && <FieldIcon Icon={Mail} label={language === 'ar' ? 'البريد' : 'Email'} value={company.email} isDarkMode={isDarkMode} />}
                      {company.activities && <FieldIcon Icon={ClipboardList} label={language === 'ar' ? 'الأنشطة' : 'Activities'} value={company.activities} isDarkMode={isDarkMode} />}
                    </div>
                  </div>
                )}

                {/* ─── CONTACT PERSONS ─── */}
                {Array.isArray(company.contact_persons) && company.contact_persons.length > 0 && (
                  <div className="cgm-ai-card" data-testid="section-contact-persons">
                    <SectionTitle Icon={Users} label={language === 'ar' ? `جهات الاتصال (${company.contact_persons.length})` : `Contact persons (${company.contact_persons.length})`} isDarkMode={isDarkMode} />
                    <div className="space-y-2">
                      {company.contact_persons.map((p, i) => (
                        <div key={i} className={`rounded-lg p-3 ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                          <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{p.name}</div>
                          {p.role && <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{p.role}</div>}
                          {(p.email || p.phone) && (
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {p.email && <span>{p.email}</span>}
                              {p.phone && <span>{p.email && ' · '}{p.phone}</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ─── TAB SWITCH (services / res+visas / emirates / other) ─── */}
                <div className="cgm-ai-card !p-4 sm:!p-5">
                  {/* iter-303 — Request new document action bar */}
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className={`h-4 w-4 ${isDarkMode ? 'text-[#38BDF8]' : 'text-blue-600'}`} />
                      <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {language === 'ar' ? 'المستندات والخدمات' : 'Documents & services'}
                      </span>
                      {docRequests.length > 0 && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-50 text-amber-700'}`}
                              data-testid="pending-doc-requests-count">
                          {language === 'ar' ? `${docRequests.length} طلب` : `${docRequests.length} request${docRequests.length === 1 ? '' : 's'}`}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setDocRequestOpen(true)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-all shadow-sm ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-[#38BDF8] to-[#F97316] text-white hover:opacity-95'
                          : 'bg-gradient-to-r from-[#38BDF8] to-[#F97316] text-white hover:opacity-95'
                      }`}
                      data-testid="btn-request-new-doc"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {language === 'ar' ? 'طلب مستند جديد' : 'Request new document'}
                    </button>
                  </div>

                  {/* Pending document requests strip */}
                  {docRequests.length > 0 && (
                    <DocRequestsStrip requests={docRequests} language={language} isDarkMode={isDarkMode} fmtDate={fmtDate} />
                  )}

                  <div className={`flex items-center gap-1.5 p-1.5 mb-4 rounded-xl overflow-x-auto ${
                    isDarkMode ? 'bg-white/5' : 'bg-gray-100'
                  }`} data-testid="company-tabs">
                    {TABS.map((tab) => {
                      const Icon = tab.Icon;
                      const active = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setActiveTab(tab.id)}
                          data-testid={`company-tab-${tab.id}`}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold whitespace-nowrap transition-all ${
                            active
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow'
                              : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          <span>{language === 'ar' ? tab.labelAr : tab.labelEn}</span>
                          {tab.count !== null && tab.count !== undefined && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                              active ? 'bg-white/25 text-white' : (isDarkMode ? 'bg-white/10 text-gray-300' : 'bg-gray-200 text-gray-700')
                            }`}>
                              {tab.count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Tab bodies */}
                  {activeTab === 'services' && (
                    <ServicesTab
                      services={services}
                      error={servicesErr}
                      language={language}
                      isDarkMode={isDarkMode}
                      fmtDate={fmtDate}
                      onOpen={(sid) => navigate(`/agent-tasks?open=${sid}`)}
                    />
                  )}
                  {activeTab === 'res_visas' && (
                    <DocListTab
                      docs={resVisas}
                      language={language}
                      isDarkMode={isDarkMode}
                      fmtDate={fmtDate}
                      daysUntil={daysUntil}
                      emptyAr="لا توجد إقامات أو تأشيرات على هذا الملف"
                      emptyEn="No residencies or visas on file"
                      downloading={downloading}
                      onDownload={(doc) => downloadFile(
                        `${BACKEND_URL}/api/companies/documents/${doc.__singular}/${doc.id}`,
                        doc.file_name || `${doc.__singular}.pdf`,
                        `${doc.__singular}:${doc.id}`,
                      )}
                      onPreview={(doc) => previewFile(
                        `${BACKEND_URL}/api/companies/documents/${doc.__singular}/${doc.id}`,
                        doc.file_name || `${doc.__singular}.pdf`,
                        `preview-${doc.__singular}:${doc.id}`,
                      )}
                      showSubKindChip
                      idNumberField="passport_number"
                      idNumberLabelAr="جواز السفر"
                      idNumberLabelEn="Passport"
                    />
                  )}
                  {activeTab === 'emirates' && (
                    <DocListTab
                      docs={emiratesIds.map(d => ({ ...d, __kind: 'emirates_id', __singular: 'emirates_id' }))}
                      language={language}
                      isDarkMode={isDarkMode}
                      fmtDate={fmtDate}
                      daysUntil={daysUntil}
                      emptyAr="لا توجد هويات إماراتية على هذا الملف"
                      emptyEn="No Emirates IDs on file"
                      downloading={downloading}
                      onDownload={(doc) => downloadFile(
                        `${BACKEND_URL}/api/companies/documents/emirates_id/${doc.id}`,
                        doc.file_name || 'emirates_id.pdf',
                        `emirates_id:${doc.id}`,
                      )}
                      onPreview={(doc) => previewFile(
                        `${BACKEND_URL}/api/companies/documents/emirates_id/${doc.id}`,
                        doc.file_name || 'emirates_id.pdf',
                        `preview-emirates_id:${doc.id}`,
                      )}
                      idNumberField="emirates_id_number"
                      idNumberLabelAr="رقم الهوية"
                      idNumberLabelEn="ID Number"
                    />
                  )}
                  {activeTab === 'other' && (
                    <OtherDocsTab
                      docs={otherDocs}
                      language={language}
                      isDarkMode={isDarkMode}
                      downloading={downloading}
                      onDownload={(doc) => downloadFile(
                        `${BACKEND_URL}/api/companies/documents/other/${doc.id}`,
                        doc.file_name || 'document.pdf',
                        `other:${doc.id}`,
                      )}
                      onPreview={(doc) => previewFile(
                        `${BACKEND_URL}/api/companies/documents/other/${doc.id}`,
                        doc.file_name || 'document.pdf',
                        `preview-other:${doc.id}`,
                      )}
                    />
                  )}
                </div>

                {/* ─── LICENSE HISTORY ─── */}
                {licenseHistory.length > 0 && (
                  <div className="cgm-ai-card" data-testid="section-license-history">
                    <SectionTitle Icon={History} label={language === 'ar' ? `سجل الرخصة (${licenseHistory.length})` : `License history (${licenseHistory.length})`} isDarkMode={isDarkMode} />
                    <div className="space-y-2">
                      {[...licenseHistory]
                        .sort((a, b) => new Date(b.issue_date || 0) - new Date(a.issue_date || 0))
                        .map((h, i) => (
                          <div key={h.id || i} className={`rounded-lg p-3 border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                            <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {language === 'ar' ? 'رقم الرخصة: ' : 'License #: '}{h.license_number || '—'}
                            </div>
                            <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {fmtDate(h.issue_date)} → {fmtDate(h.expiry_date)}
                              {h.issuing_authority && <> · {h.issuing_authority}</>}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* ─── RENEWALS ─── */}
                {renewals.length > 0 && (
                  <div className="cgm-ai-card" data-testid="section-renewals">
                    <SectionTitle Icon={Calendar} label={language === 'ar' ? `سجل التجديدات (${renewals.length})` : `Renewal history (${renewals.length})`} isDarkMode={isDarkMode} />
                    <div className="space-y-2">
                      {renewals.map((r) => (
                        <div key={r.id || r.renewal_index} className={`rounded-lg p-3 border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              #{r.renewal_index} · {fmtDate(r.issue_date)} → {fmtDate(r.expiry_date)}
                            </span>
                            {r.audited && <Badge className="bg-emerald-500 text-white text-[10px]">{language === 'ar' ? 'موثّق' : 'Audited'}</Badge>}
                          </div>
                          <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {r.license_number && <>{language === 'ar' ? 'رقم الرخصة: ' : 'License #: '}{r.license_number}</>}
                            {r.renewal_amount != null && (
                              <> · <span className="font-semibold">{Number(r.renewal_amount).toLocaleString()} AED</span></>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ─── LINKED ACCOUNTS ─── */}
                {linkedAccounts.length > 0 && (
                  <LinkedAccountsSection
                    accounts={linkedAccounts}
                    language={language}
                    isDarkMode={isDarkMode}
                  />
                )}

                {company.synced_at && (
                  <div className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {language === 'ar' ? 'آخر مزامنة: ' : 'Last synced: '}{fmtDate(company.synced_at)}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* iter-303 — Inline PDF preview modal */}
      {preview && (
        <PdfPreviewModal
          preview={preview}
          onClose={closePreview}
          onDownload={() => downloadFile(preview.downloadUrl, preview.filename, preview.downloadKey?.replace('preview-', '') || 'download')}
          language={language}
          isDarkMode={isDarkMode}
        />
      )}

      {/* iter-303 — Request new document modal */}
      {docRequestOpen && (
        <RequestDocModal
          onClose={() => setDocRequestOpen(false)}
          onSubmit={submitDocRequest}
          language={language}
          isDarkMode={isDarkMode}
        />
      )}
      {/* iter-308 — Service request modal */}
      <ServiceRequestModal
        open={serviceRequestOpen}
        onClose={() => setServiceRequestOpen(false)}
        company={company}
        language={language}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}

// ─────────────────────────── TAB BODIES ───────────────────────────

function ServicesTab({ services, error, language, isDarkMode, fmtDate, onOpen }) {
  if (services === null && !error) {
    return (
      <div className="py-8 flex justify-center" data-testid="services-loading">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }
  if (error) {
    return (
      <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-700'}`}>
        <AlertCircle className="inline h-4 w-4 me-2" />{error}
      </div>
    );
  }
  if (!services || services.length === 0) {
    return (
      <EmptyState
        Icon={Briefcase}
        ar="لا توجد خدمات مسجّلة على هذه الشركة بعد"
        en="No services registered on this company yet"
        language={language}
        isDarkMode={isDarkMode}
      />
    );
  }
  return (
    <div className="space-y-2" data-testid="services-list">
      {services.map((s) => {
        const currentStage = pickCurrentStage(s.request_stages) || s.status || 'pending_review';
        const stage = STAGE_LABELS[currentStage] || null;
        const stageLabel = stage ? (language === 'ar' ? stage.ar : stage.en) : currentStage;
        const StageIcon = stage?.Icon || Clock;
        const paid = s.paid_total_agent_visible;
        const commission = s.commission_amount_agent_visible;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onOpen(s.id)}
            className={`w-full text-start rounded-lg p-3 border transition-all hover:shadow-sm flex items-center justify-between gap-3 ${
              isDarkMode ? 'bg-white/5 border-white/10 hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
            data-testid={`service-row-${s.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full shrink-0 ${isDarkMode ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                  {s.request_no_display || s.id?.slice(0, 8)}
                </span>
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  <StageIcon className="h-3 w-3" />{stageLabel}
                </span>
              </div>
              <div className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                   title={language === 'ar' ? (s.service_name_ar || s.service_name_en) : (s.service_name_en || s.service_name_ar)}>
                {language === 'ar' ? (s.service_name_ar || s.service_name_en) : (s.service_name_en || s.service_name_ar)}
              </div>
              <div className={`text-xs mt-1 flex items-center gap-3 flex-wrap ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {s.created_at && <span>{fmtDate(s.created_at)}</span>}
                {paid != null && <span>{language === 'ar' ? 'مدفوع: ' : 'Paid: '}<b>{Number(paid).toLocaleString()} AED</b></span>}
                {commission != null && <span>{language === 'ar' ? 'العمولة: ' : 'Commission: '}<b>{Number(commission).toLocaleString()} AED</b></span>}
              </div>
            </div>
            <ChevronRight className={`h-4 w-4 shrink-0 ${language === 'ar' ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          </button>
        );
      })}
    </div>
  );
}

function DocListTab({ docs, language, isDarkMode, fmtDate, daysUntil, emptyAr, emptyEn,
  downloading, onDownload, onPreview, showSubKindChip, idNumberField, idNumberLabelAr, idNumberLabelEn }) {
  if (!docs.length) {
    return <EmptyState Icon={IdCard} ar={emptyAr} en={emptyEn} language={language} isDarkMode={isDarkMode} />;
  }
  return (
    <div className="space-y-2">
      {docs.map((d) => {
        const holder = d.full_name_ar || d.full_name_en || d.title || d.file_name || '—';
        const dLeft = daysUntil(d.expiry_date);
        const busyDl = downloading === `${d.__singular}:${d.id}`;
        const busyPv = downloading === `preview-${d.__singular}:${d.id}`;
        return (
          <div key={`${d.__kind}-${d.id}`} className={`rounded-lg p-3 border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}
               data-testid={`doc-row-${d.__kind}-${d.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <div className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} title={holder}>
                    {language === 'ar' ? (d.full_name_ar || holder) : (d.full_name_en || holder)}
                  </div>
                  {showSubKindChip && (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${
                      d.__kind === 'residency'
                        ? (isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-50 text-blue-700')
                        : (isDarkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-50 text-purple-700')
                    }`}>
                      {d.__kind === 'residency' ? (language === 'ar' ? 'إقامة' : 'Residency') : (language === 'ar' ? 'تأشيرة' : 'Visa')}
                    </span>
                  )}
                  {dLeft !== null && (
                    dLeft < 0
                      ? <Badge className="bg-red-500 text-white text-[10px]">{language === 'ar' ? 'منتهية' : 'Expired'}</Badge>
                      : dLeft <= 30
                        ? <Badge className="bg-amber-500 text-white text-[10px]">{language === 'ar' ? `${dLeft} يوم` : `${dLeft}d`}</Badge>
                        : <Badge className="bg-emerald-500 text-white text-[10px]">{language === 'ar' ? 'سارية' : 'Active'}</Badge>
                  )}
                </div>
                <div className={`text-xs flex flex-wrap gap-x-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {d.nationality && <span>{d.nationality}</span>}
                  {idNumberField && d[idNumberField] && (
                    <span>· {language === 'ar' ? idNumberLabelAr : idNumberLabelEn}: {d[idNumberField]}</span>
                  )}
                  {d.issue_date && <span>· {fmtDate(d.issue_date)}</span>}
                  {d.expiry_date && <span>→ {fmtDate(d.expiry_date)}</span>}
                </div>
              </div>
              {d.has_file && (
                <div className="flex items-center gap-1.5 shrink-0">
                  {onPreview && (
                    <button
                      type="button"
                      onClick={() => onPreview(d)}
                      disabled={busyPv}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all disabled:opacity-60 ${
                        isDarkMode ? 'bg-white/10 text-white hover:bg-white/15' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`preview-doc-${d.__kind}-${d.id}`}
                    >
                      {busyPv ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                      {language === 'ar' ? 'معاينة' : 'Preview'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDownload(d)}
                    disabled={busyDl}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all disabled:opacity-60 ${
                      isDarkMode ? 'bg-[#38BDF8]/15 text-[#38BDF8] hover:bg-[#38BDF8]/25' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                    data-testid={`download-doc-${d.__kind}-${d.id}`}
                  >
                    {busyDl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    {language === 'ar' ? 'تحميل' : 'Download'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OtherDocsTab({ docs, language, isDarkMode, downloading, onDownload, onPreview }) {
  if (!docs.length) {
    return <EmptyState Icon={FolderOpen}
      ar="لا توجد وثائق أخرى على هذا الملف"
      en="No other documents on file"
      language={language} isDarkMode={isDarkMode} />;
  }
  return (
    <div className="space-y-2">
      {docs.map((d) => {
        const title = language === 'ar' ? (d.title_ar || d.title || d.file_name) : (d.title_en || d.title || d.file_name);
        const busyDl = downloading === `other:${d.id}`;
        const busyPv = downloading === `preview-other:${d.id}`;
        return (
          <div key={d.id} className={`rounded-lg p-3 border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}
               data-testid={`doc-row-other-${d.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} title={title}>{title}</div>
                {d.notes && (
                  <div className={`text-xs mt-1 italic ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{d.notes}</div>
                )}
              </div>
              {d.has_file && (
                <div className="flex items-center gap-1.5 shrink-0">
                  {onPreview && (
                    <button
                      type="button"
                      onClick={() => onPreview(d)}
                      disabled={busyPv}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all disabled:opacity-60 ${
                        isDarkMode ? 'bg-white/10 text-white hover:bg-white/15' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`preview-doc-other-${d.id}`}
                    >
                      {busyPv ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                      {language === 'ar' ? 'معاينة' : 'Preview'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDownload(d)}
                    disabled={busyDl}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all disabled:opacity-60 ${
                      isDarkMode ? 'bg-[#38BDF8]/15 text-[#38BDF8] hover:bg-[#38BDF8]/25' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                    data-testid={`download-doc-other-${d.id}`}
                  >
                    {busyDl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    {language === 'ar' ? 'تحميل' : 'Download'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────── LINKED ACCOUNTS ───────────────────────────

function LinkedAccountsSection({ accounts, language, isDarkMode }) {
  // Own email = session storage user's email.
  let ownEmail = '';
  try {
    const raw = localStorage.getItem('business_user');
    if (raw) ownEmail = (JSON.parse(raw).email || '').toLowerCase();
  } catch { /* ignore */ }

  const maskPhone = (p) => {
    if (!p) return '';
    const s = String(p);
    if (s.length <= 4) return s;
    // Show last 4, mask the rest.
    return s.slice(0, -4).replace(/./g, '•') + s.slice(-4);
  };

  return (
    <div className="cgm-ai-card" data-testid="section-linked-accounts">
      <SectionTitle Icon={ShieldCheck} label={language === 'ar' ? `الحسابات المشاركة (${accounts.length})` : `Linked accounts (${accounts.length})`} isDarkMode={isDarkMode} />
      <div className="space-y-2">
        {accounts.map((acc, i) => {
          const isMe = (acc.email || '').toLowerCase() === ownEmail;
          return (
            <div key={i} className={`rounded-lg p-3 flex items-center justify-between gap-3 ${
              isMe
                ? (isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-300')
                : (isDarkMode ? 'bg-white/5' : 'bg-gray-50')
            }`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} title={acc.email}>
                    {acc.email || '—'}
                  </span>
                  {isMe && (
                    <Badge className="bg-emerald-500 text-white text-[10px]">
                      {language === 'ar' ? 'أنت' : 'You'}
                    </Badge>
                  )}
                </div>
                {acc.phone && (
                  <div className={`text-xs font-mono mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {maskPhone(acc.phone)}
                  </div>
                )}
              </div>
              {acc.status && (
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${
                  acc.status === 'registered'
                    ? (isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700')
                    : (isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-50 text-amber-700')
                }`}>
                  {acc.status}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────── SMALL HELPERS ───────────────────────────

function pickCurrentStage(rs) {
  if (!rs || typeof rs !== 'object') return null;
  let current = null;
  for (const key of STAGE_ORDER) {
    if (rs[key]?.done) current = key;
  }
  return current;
}

function ExpiryBadge({ expiryDate, language, daysUntil }) {
  const d = daysUntil(expiryDate);
  if (d === null) return null;
  if (d < 0) return <Badge className="bg-red-500 text-white shrink-0">{language === 'ar' ? 'منتهية' : 'Expired'}</Badge>;
  if (d <= 30) return <Badge className="bg-amber-500 text-white shrink-0">{language === 'ar' ? `${d} يوم` : `${d}d`}</Badge>;
  return <Badge className="bg-emerald-500 text-white shrink-0">{language === 'ar' ? 'سارية' : 'Active'}</Badge>;
}

function SectionTitle({ Icon, label, isDarkMode }) {
  return (
    <h2 className={`flex items-center gap-2 text-sm font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
      <Icon className={`h-4 w-4 ${isDarkMode ? 'text-[#38BDF8]' : 'text-blue-600'}`} />
      {label}
    </h2>
  );
}

function Field({ label, value, isDarkMode }) {
  // iter-288 — hide fields without data (no "—" rows)
  if (value === null || value === undefined || value === '' || value === '—') return null;
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{label}</div>
      <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} title={String(value)}>{value}</div>
    </div>
  );
}

function FieldIcon({ Icon, label, value, isDarkMode }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
      <div className="min-w-0">
        <div className={`text-[10px] uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{label}</div>
        <div className={`text-sm break-words ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</div>
      </div>
    </div>
  );
}

function EmptyState({ Icon, ar, en, language, isDarkMode }) {
  return (
    <div className={`py-10 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
      <Icon className="h-10 w-10 mx-auto mb-2 opacity-50" />
      <p className="text-sm">{language === 'ar' ? ar : en}</p>
    </div>
  );
}

// ─────────────────────── iter-303 NEW COMPONENTS ───────────────────────

/**
 * ExpiryCountdownCard — large prominent card at top of company detail
 * showing days until license expiry with a progress bar and CTA.
 * Updates live every minute. Uses CloudGM primary/accent palette.
 */
function ExpiryCountdownCard({ expiryDate, language, isDarkMode, fmtDate, daysUntil }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const d = daysUntil(expiryDate);
  if (d === null) return null;

  const expired = d < 0;
  const urgent = !expired && d <= 30;
  const warn = !expired && d > 30 && d <= 90;

  const palette = expired
    ? { bg: 'from-red-500 to-rose-600', text: 'text-white', iconBg: 'bg-white/20', barFill: 'bg-white' }
    : urgent
      ? { bg: 'from-amber-500 to-orange-600', text: 'text-white', iconBg: 'bg-white/20', barFill: 'bg-white' }
      : warn
        ? { bg: 'from-sky-500 to-cyan-600', text: 'text-white', iconBg: 'bg-white/20', barFill: 'bg-white' }
        : { bg: 'from-emerald-500 to-teal-600', text: 'text-white', iconBg: 'bg-white/20', barFill: 'bg-white' };

  // Progress bar — assume a 365-day license term by default, clamp 0..1.
  const total = 365;
  const used = Math.min(total, Math.max(0, total - d));
  const pct = Math.min(100, Math.max(0, (used / total) * 100));

  const absDays = Math.abs(d);
  const title = expired
    ? (language === 'ar' ? 'الرخصة منتهية' : 'License expired')
    : urgent
      ? (language === 'ar' ? 'الرخصة تنتهي قريباً' : 'License expiring soon')
      : warn
        ? (language === 'ar' ? 'اقتراب تجديد الرخصة' : 'Renewal approaching')
        : (language === 'ar' ? 'الرخصة سارية' : 'License active');

  const subtitle = expired
    ? (language === 'ar' ? `منذ ${absDays} يوم` : `${absDays} days ago`)
    : (language === 'ar' ? `متبقٍ ${absDays} يوم` : `${absDays} days remaining`);

  return (
    <div
      className={`mt-4 rounded-2xl p-4 sm:p-5 bg-gradient-to-br ${palette.bg} ${palette.text} relative overflow-hidden`}
      data-testid="expiry-countdown-card"
    >
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />

      <div className="relative flex items-center gap-3">
        <div className={`shrink-0 w-12 h-12 rounded-full ${palette.iconBg} backdrop-blur border border-white/30 flex items-center justify-center`}>
          <Timer className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider opacity-80 font-semibold">{title}</span>
            {expired && <AlertTriangle className="h-3.5 w-3.5" />}
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className="text-3xl font-extrabold leading-none tabular-nums" data-testid="countdown-days">
              {absDays}
            </span>
            <span className="text-xs opacity-90">
              {language === 'ar' ? 'يوم' : (absDays === 1 ? 'day' : 'days')}
            </span>
            <span className="text-xs opacity-75 hidden sm:inline">· {subtitle}</span>
          </div>
          <div className="text-[11px] opacity-85 mt-1">
            {language === 'ar' ? 'ينتهي في' : 'Expires on'} {fmtDate(expiryDate)}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      {!expired && (
        <div className="relative mt-3 h-1.5 rounded-full bg-white/20 overflow-hidden" role="progressbar" aria-valuenow={Math.round(pct)}>
          <div
            className={`h-full ${palette.barFill} rounded-full transition-all duration-500`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * DocRequestsStrip — slim horizontal list of pending document requests.
 */
function DocRequestsStrip({ requests, language, isDarkMode, fmtDate }) {
  const KIND_LABELS = {
    license: language === 'ar' ? 'رخصة' : 'License',
    residency: language === 'ar' ? 'إقامة' : 'Residency',
    visa: language === 'ar' ? 'تأشيرة' : 'Visa',
    emirates_id: language === 'ar' ? 'هوية إماراتية' : 'Emirates ID',
    other: language === 'ar' ? 'أخرى' : 'Other',
  };
  const STATUS_LABELS = {
    pending: { ar: 'قيد المراجعة', en: 'Pending',  cls: isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-50 text-amber-700' },
    in_progress: { ar: 'قيد التنفيذ', en: 'In progress', cls: isDarkMode ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-50 text-sky-700' },
    completed: { ar: 'مكتمل', en: 'Completed', cls: isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700' },
    rejected: { ar: 'مرفوض', en: 'Rejected', cls: isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-50 text-red-700' },
  };

  return (
    <div className="mb-4 space-y-1.5" data-testid="doc-requests-strip">
      {requests.slice(0, 5).map((r) => {
        const st = STATUS_LABELS[r.status] || STATUS_LABELS.pending;
        return (
          <div
            key={r.id}
            className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ${
              isDarkMode ? 'bg-white/5 border-white/10' : 'bg-amber-50/40 border-amber-100'
            }`}
            data-testid={`doc-request-row-${r.id}`}
          >
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${isDarkMode ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                {KIND_LABELS[r.doc_kind] || r.doc_kind}
              </span>
              <span className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} title={r.title}>
                {r.title}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>
                {language === 'ar' ? st.ar : st.en}
              </span>
              <span className={`text-[10px] whitespace-nowrap ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {fmtDate(r.created_at)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * PdfPreviewModal — inline iframe preview of a PDF blob. Allows the
 * user to decide whether to download after they've looked.
 */
function PdfPreviewModal({ preview, onClose, onDownload, language, isDarkMode }) {
  // Close on ESC.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      data-testid="pdf-preview-modal"
    >
      <div
        className={`w-full max-w-5xl max-h-[94vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden ${
          isDarkMode ? 'bg-[#131B2C]' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex items-center justify-between px-4 py-3 border-b ${
            isDarkMode ? 'border-white/10' : 'border-gray-100'
          }`}
          style={{ background: 'linear-gradient(90deg, rgba(56,189,248,0.08) 0%, rgba(249,115,22,0.08) 100%)' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Eye className="h-4 w-4 shrink-0 text-[#38BDF8]" />
            <h3 className={`text-sm font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`} title={preview.filename}>
              {preview.filename}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#38BDF8] to-[#F97316] text-white text-xs font-bold hover:opacity-95 transition-opacity"
              data-testid="btn-preview-download"
            >
              <Download className="h-3.5 w-3.5" />
              {language === 'ar' ? 'تحميل' : 'Download'}
            </button>
            <button
              onClick={onClose}
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isDarkMode ? 'text-gray-400 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'
              }`}
              aria-label="close"
              data-testid="btn-preview-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className={`flex-1 ${isDarkMode ? 'bg-black/40' : 'bg-gray-50'}`}>
          <iframe
            title="pdf-preview"
            src={preview.url}
            className="w-full h-[80vh] bg-white"
            data-testid="pdf-preview-iframe"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * RequestDocModal — form for requesting a new document from the agent.
 */
function RequestDocModal({ onClose, onSubmit, language, isDarkMode }) {
  const [docKind, setDocKind] = useState('license');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const KINDS = [
    { id: 'license',     labelAr: 'رخصة تجارية', labelEn: 'Trade license' },
    { id: 'residency',   labelAr: 'إقامة',       labelEn: 'Residency' },
    { id: 'visa',        labelAr: 'تأشيرة',       labelEn: 'Visa' },
    { id: 'emirates_id', labelAr: 'هوية إماراتية', labelEn: 'Emirates ID' },
    { id: 'other',       labelAr: 'أخرى',         labelEn: 'Other' },
  ];
  const URGENCIES = [
    { id: 'low',    labelAr: 'منخفض',  labelEn: 'Low' },
    { id: 'normal', labelAr: 'عادي',  labelEn: 'Normal' },
    { id: 'high',   labelAr: 'عاجل',  labelEn: 'Urgent' },
  ];

  // Close on ESC.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError(language === 'ar' ? 'العنوان مطلوب' : 'Title is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        doc_kind: docKind,
        title: title.trim(),
        notes: notes.trim(),
        urgency,
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || (language === 'ar' ? 'تعذّر إرسال الطلب' : 'Failed to submit request'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      data-testid="request-doc-modal"
    >
      <div
        className={`w-full max-w-md max-h-[94vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden ${
          isDarkMode ? 'bg-[#131B2C]' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}
          style={{ background: 'linear-gradient(90deg, rgba(56,189,248,0.08) 0%, rgba(249,115,22,0.08) 100%)' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Plus className="h-4 w-4 shrink-0 text-[#F97316]" />
            <h3 className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {language === 'ar' ? 'طلب مستند جديد' : 'Request a new document'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isDarkMode ? 'text-gray-400 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="close"
            data-testid="btn-request-doc-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4 overflow-y-auto">
          {/* Kind */}
          <div>
            <label className={`block text-xs font-bold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {language === 'ar' ? 'نوع المستند' : 'Document type'}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {KINDS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => setDocKind(k.id)}
                  data-testid={`req-kind-${k.id}`}
                  className={`text-xs font-semibold py-2 rounded-lg transition-all border ${
                    docKind === k.id
                      ? 'bg-gradient-to-r from-[#38BDF8] to-[#F97316] text-white border-transparent shadow'
                      : isDarkMode
                        ? 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {language === 'ar' ? k.labelAr : k.labelEn}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className={`block text-xs font-bold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {language === 'ar' ? 'عنوان الطلب' : 'Request title'} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder={language === 'ar' ? 'مثال: نسخة جديدة من الرخصة التجارية' : 'e.g. New copy of trade license'}
              className={`w-full text-sm rounded-lg px-3 py-2.5 border focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/60 ${
                isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
              }`}
              data-testid="req-title-input"
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label className={`block text-xs font-bold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder={language === 'ar' ? 'أي تفاصيل إضافية…' : 'Any additional details…'}
              className={`w-full text-sm rounded-lg px-3 py-2.5 border focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/60 resize-none ${
                isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
              }`}
              data-testid="req-notes-input"
            />
          </div>

          {/* Urgency */}
          <div>
            <label className={`block text-xs font-bold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {language === 'ar' ? 'الأولوية' : 'Urgency'}
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {URGENCIES.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setUrgency(u.id)}
                  data-testid={`req-urgency-${u.id}`}
                  className={`text-xs font-semibold py-2 rounded-lg transition-all border ${
                    urgency === u.id
                      ? 'bg-gradient-to-r from-[#38BDF8] to-[#F97316] text-white border-transparent shadow'
                      : isDarkMode
                        ? 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {language === 'ar' ? u.labelAr : u.labelEn}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 rounded-lg p-2.5 text-xs bg-red-50 border border-red-200 text-red-700" data-testid="req-error">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={`flex-1 text-sm font-semibold py-2.5 rounded-lg border transition-colors ${
                isDarkMode ? 'bg-transparent text-gray-300 border-white/10 hover:bg-white/5' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
              data-testid="btn-request-doc-cancel"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-lg text-white bg-gradient-to-r from-[#38BDF8] to-[#F97316] hover:opacity-95 disabled:opacity-60 transition-all shadow"
              data-testid="btn-request-doc-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {language === 'ar' ? 'إرسال الطلب' : 'Submit request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
